import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCreateCompany } from "@/hooks/useCompanies";
import { Database } from "@/integrations/neon/types";
import { SUPPORTED_LANGUAGES } from "@/lib/languages";

type CompanyType = Database["public"]["Enums"]["company_type"];

const companyTypes: { value: CompanyType; label: string }[] = [
  { value: "customer", label: "Customer" },
  { value: "provider", label: "Provider" },
];

const inlineCompanySchema = z.object({
  name: z.string().min(1, "Name is required").max(255, "Name is too long"),
  types: z
    .array(z.enum(["customer", "provider", "main_company"]))
    .default(["customer"]),
  invoice_address: z
    .string()
    .max(1000, "Address is too long")
    .nullable()
    .optional(),
  language: z.string().nullable().optional(),
});

type InlineCompanyFormValues = z.infer<typeof inlineCompanySchema>;

interface InlineCompanyFormProps {
  onSuccess: (newId: string) => void;
  onCancel: () => void;
  initialName?: string;
  defaultTypes?: CompanyType[];
}

export function InlineCompanyForm({
  onSuccess,
  onCancel,
  initialName = "",
  defaultTypes = ["customer"],
}: InlineCompanyFormProps) {
  const createCompany = useCreateCompany();

  const form = useForm<InlineCompanyFormValues>({
    resolver: zodResolver(inlineCompanySchema),
    defaultValues: {
      name: initialName,
      types: defaultTypes,
      invoice_address: "",
      language: null,
    },
  });

  const handleSubmit = async (values: InlineCompanyFormValues) => {
    try {
      const result = await createCompany.mutateAsync({
        name: values.name,
        types: values.types,
        invoice_address: values.invoice_address || null,
        language: values.language || null,
      });
      onSuccess(result.id);
    } catch (error) {
      // Error is handled by the mutation
    }
  };

  return (
    <Form {...form}>
      <form
        onSubmit={(e) => {
          e.stopPropagation();
          form.handleSubmit(handleSubmit)(e);
        }}
        className="space-y-4"
      >
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Company Name *</FormLabel>
              <FormControl>
                <Input placeholder="Company name" {...field} autoFocus />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="invoice_address"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Invoice Address</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Enter invoice address"
                  {...field}
                  value={field.value ?? ""}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="language"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Language</FormLabel>
              <Select
                onValueChange={field.onChange}
                value={field.value ?? undefined}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select language" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {SUPPORTED_LANGUAGES.map((lang) => (
                    <SelectItem key={lang.value} value={lang.value}>
                      {lang.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="types"
          render={() => (
            <FormItem>
              <FormLabel>Company Types</FormLabel>
              <div className="flex flex-wrap gap-4">
                {companyTypes.map((type) => (
                  <FormField
                    key={type.value}
                    control={form.control}
                    name="types"
                    render={({ field }) => (
                      <FormItem className="flex items-center space-x-2 space-y-0">
                        <FormControl>
                          <Checkbox
                            checked={field.value?.includes(type.value)}
                            onCheckedChange={(checked) => {
                              const current = field.value || [];
                              if (checked) {
                                field.onChange([...current, type.value]);
                              } else {
                                field.onChange(
                                  current.filter((v) => v !== type.value),
                                );
                              }
                            }}
                          />
                        </FormControl>
                        <FormLabel className="font-normal cursor-pointer">
                          {type.label}
                        </FormLabel>
                      </FormItem>
                    )}
                  />
                ))}
              </div>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end gap-3 pt-4 border-t border-border">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" disabled={createCompany.isPending}>
            {createCompany.isPending ? "Creating..." : "Create Company"}
          </Button>
        </div>
      </form>
    </Form>
  );
}
